import './DodDeletedTable.scss';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ByzzerTable } from '@/components/ByzzerTable';
import { useTenantApi } from '@/hooks/useTenantApi';
import { confirm, openErrorModal, alert as byzzerAlert } from '@/components/form/ByzzerModal'; // Uncomment when functionality is implemented as part of BYZ-15013
import { formatInTimeZone } from 'date-fns-tz';
import DeleteIcon from '@images/icons/niq-trash-dark.svg';
import DeleteIconDisabled from '@images/icons/niq-trash-disabled-color.svg';
import {getCreatedDtm, getTimestamp, calculateDiffInDays } from '@/components/DodConfigEditor/common/utils';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { ByzColDef } from '@/types/TableTypes';
import RestoreIcon from '@images/icons/niq-reset-dark.svg';
import RestoreIconDisabled from '@images/icons/niq-reset-disabled-color.svg';
import { useUser } from '@/contexts/UserContext';
import { frameTimePeriod } from '@/services/extract.service';
import { getYearsFromSubscriptionData } from '@/utils/timeperiodUtil';
import moment from 'moment';
import { DodDeletedExtractsResponse } from '@/types/ApiTypes';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Import Tippy styles
import { DAYS_PASSED_DELETE_ACTION_HOVER_MESSAGE, DAYS_PASSED_RESTORE_ACTION_HOVER_MESSAGE, DELETED_ACTION_HOVER_MESSAGE, RESTORE_ACTION_HOVER_MESSAGE, SCHEDULED_RESTORE_ACTION_HOVER_MESSAGE } from '@/constants/dod.constants';
import { FLOATING_DATE_PICKER_FILTER_PARAMS } from '@/constants/table.constants';

type DodDeletedTableColId =
    | 'checkbox'
    | 'runName'
    | 'status'
    | 'type'
    | 'daysRemaining'
    | 'deletedDate'
    | 'deletedTimestamp'
    | 'deletedBy'
    | 'runDate'
    | 'timeStamp'
    | 'runBy'
    | 'seriesName'
    | 'timePeriod'
    | 'categories'
    | 'menu';

const baseClass = 'dod-deleted-table';
type DodDeletedTableProps = {
    dodTemplatesRef: {
        current: any[];
    };
    remainingCoreRuns: number;
    handleSelectionChange: (selectedData: any[]) => void;
    deletedExtracts: DodDeletedExtractsResponse[];
    setDeletedExtracts: (data: DodDeletedExtractsResponse[]) => void;
};

const DodDeletedTable = ({ handleSelectionChange, deletedExtracts, setDeletedExtracts, ...props }: DodDeletedTableProps) => {
    const { getDodDeletedData, dodRestoreExtract, permanentlyDeleteDodReports } = useTenantApi();
    const { features, user } = useUser();

    const [loading, setLoading] = useState(false);
    // const [deletedExtracts, setDeletedExtracts] = useState<DodDeletedExtractsResponse[]>([]);
    const yrs = getYearsFromSubscriptionData(features?.extendedDataYears);
    const userRole = user?.role ?? 'user';
    const defaultDateFormat = 'yyyy-MM-dd';

    const isUserMode = () => {
        return userRole !== 'viewer';
    };

    const fetchDodDeletedData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getDodDeletedData();
            // Sort the response by deleteDate in descending order
            const sortedResponse = [...response].sort((a, b) => {
                return new Date(b.deleteDate).getTime() - new Date(a.deleteDate).getTime();
            });
            const formattedResponse = sortedResponse.map((item) => ({
                ...item,
                createdDtm: getCreatedDtm(item),
                timeStamp: getTimestamp(item),
                deleteDate:
                    item?.deleteDate && moment(item?.deleteDate).isValid()
                        ? formatInTimeZone(new Date(item?.deleteDate), 'GMT', defaultDateFormat) : 'NA',
                deletedTimestamp:
                    item?.deleteDate && moment(item?.deleteDate).isValid()
                        ? new Date(item?.deleteDate).toLocaleTimeString() : 'NA',
                daysPassedAfterDeletion: item?.deleteDate && item?.deleteDate !== null ? calculateDiffInDays(item.deleteDate) : 0
            }));
            setDeletedExtracts(formattedResponse);
        } catch (error) {
            console.error('Error fetching dod deleted extracts:', error);
        } finally {
            setLoading(false);
        }
    }, [getDodDeletedData]);

    const loadDeletedReports = async () => {
        try {
            setLoading(true);
            const response = await getDodDeletedData();
            const formattedTemplates = response.map((item) => ({
                ...item,
                createdDtm: getCreatedDtm(item),
                timeStamp: getTimestamp(item),
                deleteDate:
                    item?.deleteDate && moment(item?.deleteDate).isValid()
                        ? new Date(item?.deleteDate).toLocaleDateString() : 'NA',
                deletedTimestamp:
                    item?.deleteDate && moment(item?.deleteDate).isValid()
                        ? new Date(item?.deleteDate).toLocaleTimeString() : 'NA',
            }));
            setDeletedExtracts(formattedTemplates);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dod deleted extracts:', error);
            setLoading(false);
        } finally {
            console.log('Fetch DOD deleted extracts completed');
        }
    };

    useEffect(() => {
        handleSelectionChange([]);
        fetchDodDeletedData();
    }, []);

    const gridRef = useRef<AgGridReact>(null);
    const defaultColDef = useMemo(
        () => ({
            filter: true, // make every column use 'text' filter by default
            sortable: true,
            floatingFilter: true, // enable floating filters by default
            resizable: true, //set each col resizable
            width: 200, // set every column width
            flex: 0,
            autoHeight: false,
            wrapText: false,
            suppressMenu: true,
            lockVisible: true
        }),
        []
    );

    // The Restore Extract functionality will be implemented as part of BYZ-15013.

    const onRestoreExtract = async (data) => {
        try {
            const result = await confirm({
                title: 'Restore Extract',
                content: `Are you sure you want to restore the extract "${data?.reportName}"?`,
            });
            if (result) {
                await dodRestoreExtract(data?.id);
                fetchDodDeletedData();
            }
        } catch (error) {
            console.error('Error restoring extract:', error);
        }
    };

    const permanentlyDeleteExtract = async (id: number) => {
        try {
            if (
                !(await confirm({
                    title: 'Permanently Delete DOD Report',
                    content: <div className="byzzer-allocation-warning">Are you sure you want to permanently delete this Data On Demand run? You will not be able to restore the run if you proceed.</div>,
                    yesLabel: 'Yes',
                    noLabel: 'No',
                }))
            ) {
                return;
            }
            setLoading(true);
            let resp = await permanentlyDeleteDodReports([id]);
            if (resp?.success === true && resp?.deleted > 0) {
                await loadDeletedReports();
            }
            setLoading(false);
            let alertContent = resp?.deleted > 0 ? 'Report permanently deleted successfully!' : 'Something went wrong';
            byzzerAlert({
                // @ts-ignore
                content: <div className="report_modal_alertText">{alertContent}</div>,
            });
        } catch (err: any) {
            openErrorModal({
                title: `Something Unexpected Happened`,
                content: (
                    <>
                        <p>Fear not our engineering team is on the job.</p>
                    </>
                ),
                errorId: err.id,
            });
            setLoading(false);
        }
    }

    const updateSelectedRows = () => {
        const selectedNodes = gridRef?.current?.api?.getSelectedNodes();
        const selectedData = selectedNodes?.map(node => node.data);
        handleSelectionChange(selectedData || []);
    };
    
    useEffect(() => {
        if (gridRef.current?.api) {
            gridRef.current?.api?.addEventListener('selectionChanged', updateSelectedRows);
        }
        return () => {
            if (gridRef.current?.api) {
                gridRef.current?.api.removeEventListener('selectionChanged', updateSelectedRows);
            }
        };
    }, [deletedExtracts]);

    const [columnDefs] = useState<ByzColDef<DodDeletedExtractsResponse, DodDeletedTableColId>[]>([
        {
            colId: 'checkbox',
            headerCheckboxSelection: true,
            checkboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            sortable: false,
            filter: false,
            resizable: false,
            pinned: 'left', 
            lockPosition: true,
            width: 50,
            maxWidth: 50,
            minWidth: 50,
            disableHide: true
        },
        {
            colId: 'runName',
            field: 'reportName',
            headerName: 'Run Name',
            width: 300,
            valueGetter: ({ data }) => {
                return `${data?.reportName}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.reportName}`;
            },
            pinned: 'left',
            lockPosition: true,
            disableHide: true,
        },
        {
            colId: 'status',
            width: 300,
            headerName: 'Status',
            valueGetter: ({ data }) => {
                return `${
                    data?.extractStatus === 'Aborted'
                        ? 'Failed'
                        : data?.extractStatus === 'Scheduled Extract'
                        ? 'Scheduled'
                        : data?.extractStatus
                }`;
            },
        },
        {
            colId: 'type',
            field: 'type',
            headerName: 'Type',
            width: 300,
            valueGetter: ({ data }) => {
                return `${data?.type ?? ''}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.type ?? ''}`;
            },
        },
        {
            colId: 'daysRemaining',
            headerName: 'Days Remaining',
            valueGetter: ({ data }) => {
                return `${data?.daysPassedAfterDeletion != null ? data?.daysPassedAfterDeletion < 30 ? 30 - data?.daysPassedAfterDeletion : 90 - data?.daysPassedAfterDeletion : 'NA'}`;
            },
        },
        {
            ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            colId: 'deletedDate',
            field: 'deleteDate',
            headerName: 'Deleted Date',
           valueFormatter: ({ data }) => {
                return `${data?.deleteDate}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.deleteDate}`;
            },
        },
        {
            colId: 'deletedTimestamp',
            field: 'deletedTimestamp',
            headerName: 'Deleted Timestamp',
            valueGetter: ({ data }) => {
                return `${data?.deletedTimestamp ?? 'NA'}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.deletedTimestamp ?? 'NA'}`;
            },
        },
        {
            colId: 'deletedBy',
            headerName: 'Deleted By',
            valueGetter: ({ data }) => {
                return `${data?.deletedBy?.firstName} ${data?.deletedBy?.lastName}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.deletedBy?.firstName} ${data?.deletedBy?.lastName}`;
            },
        },
        {
            ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            colId: 'runDate',
            field: 'createdDtm',
            headerName: 'Run Date',
            valueFormatter: ({ data }) => {
                return `${data?.createdDtm}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.createdDtm}`;
            },
        },
        {
            colId: 'timeStamp',
            field: 'timeStamp',
            headerName: 'Timestamp',
            valueGetter: ({ data }) => {
                return `${data?.timeStamp}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.timeStamp}`;
            },
        },
        {
            colId: 'runBy',
            headerName: 'Run By',
            valueGetter: ({ data }) => {
                return `${data?.user?.firstName} ${data?.user?.lastName}`;
            },
        },
        {
            colId: 'seriesName',
            headerName: 'Series Name',
            valueGetter: ({ data }) => {
                return `${
                    data?.extractInfo?.scheduledInfo?.seriesName
                        ? data?.extractInfo?.scheduledInfo?.seriesName
                        : data?.extractInfo?.seriesName
                        ? data?.extractInfo?.seriesName
                        : ''
                }`;
            },
        },
        {
            colId: 'timePeriod',
            headerName: 'Time Period',
            width: 350,
            valueGetter: ({ data }) => frameTimePeriod(data.ExtractSelection, yrs),
            tooltipValueGetter: ({ data }) => frameTimePeriod(data.ExtractSelection, yrs),
        },
        {
            colId: 'categories',
            headerName: 'Category',
            width: 350,
            valueGetter: ({ data }) => {
                const categoriesData = data?.ExtractSelection?.products?.categories;
                const isExcluded = data?.ExtractSelection?.products?.categories?.isExcluded ?? false;
                const categories = Array.isArray(categoriesData)
                    ? categoriesData.join(', ')
                    : Array.isArray(categoriesData?.selections)
                    ? isExcluded
                        ? `All Except ${categoriesData.selections.join(', ')}`
                        : categoriesData.selections.join(', ')
                    : typeof categoriesData?.selections === 'boolean' &&
                      (isExcluded ? `All Except ${categoriesData?.selections}` : categoriesData?.selections)
                    ? 'ALL'
                    : '';

                const summedCategories = categoriesData?.summedSelections
                    // @ts-ignore: summedSelections is not typed
                    ?.filter((sc) => sc?.display).map((sc) => `${sc.display} (sum)`)
                    .join(', ');

                return [categories, summedCategories].filter(Boolean).join(', ');
            },
            tooltipValueGetter: ({ data }) => {
                const categoriesData = data?.ExtractSelection?.products?.categories;
                const isExcluded = data?.ExtractSelection?.products?.categories?.isExcluded ?? false;
                const categories = Array.isArray(categoriesData)
                    ? categoriesData.join(', ')
                    : Array.isArray(categoriesData?.selections)
                    ? isExcluded
                        ? `All Except ${categoriesData.selections.join(', ')}`
                        : categoriesData.selections.join(', ')
                    : typeof categoriesData?.selections === 'boolean' &&
                      (isExcluded ? `All Except ${categoriesData?.selections}` : categoriesData?.selections)
                    ? 'ALL'
                    : '';

                const summedCategories = categoriesData?.summedSelections
                    // @ts-ignore: summedSelections is not typed
                    ?.filter((sc) => sc?.display).map((sc) => `${sc.display} (sum)`)
                    .join(', ');

                return [categories, summedCategories].filter(Boolean).join(', ');
            },
            filterParams: {
                textMatcher: ({ filterOption, filterText, data }) => {
                    const value = data?.categories?.join(', ')?.toLowerCase() || '';
                    if (filterText == null) {
                        return false;
                    }
                    switch (filterOption) {
                        case 'contains':
                            return value.indexOf(filterText?.toLowerCase() ?? '') >= 0;
                        case 'notContains':
                            return value.indexOf(filterText) < 0;
                        case 'equals':
                            return value === filterText;
                        case 'notEqual':
                            return value != filterText;
                        case 'startsWith':
                            return value.indexOf(filterText) === 0;
                        case 'endsWith':
                            const index = value.lastIndexOf(filterText);
                            return index >= 0 && index === value.length - filterText.length;
                        default:
                            return false;
                    }
                },
            },
        },
        {
            headerName: 'Actions',
            colId: 'menu',
            suppressMenu: true,
            filter: false,
            floatingFilter: false,
            resizable: false,
            pinned: 'right',
            sortable: false,
            width: 150,
            cellRenderer: (params) => {
                const { data } = params;

                // Check if actions should be disabled
                const isScheduled = data?.type === 'Scheduled' && data?.status === 'Scheduled';
                const isDisabled = data?.daysPassedAfterDeletion > 30 || !isUserMode();
                const isRestoreDisabled = isScheduled || isDisabled;
                
                return (
                    <div className={`${baseClass}__actions-cell`}>
                        {/* Restore Action */}
                        {isRestoreDisabled ? (
                            <Tippy
                                content={
                                    !isUserMode()
                                        ? RESTORE_ACTION_HOVER_MESSAGE
                                        : isScheduled
                                        ? SCHEDULED_RESTORE_ACTION_HOVER_MESSAGE
                                        : DAYS_PASSED_RESTORE_ACTION_HOVER_MESSAGE
                                }
                            >
                                <span
                                    onClick={() => {
                                        if (!isRestoreDisabled) {
                                            onRestoreExtract(data);
                                        }
                                    }}
                                    style={{ cursor: isRestoreDisabled ? 'not-allowed' : 'pointer' }}
                                >
                                    <img
                                        alt="Restore"
                                        src={isRestoreDisabled ? RestoreIconDisabled : RestoreIcon}
                                        style={{ marginRight: '10px', height: '21px', width: '21px' }}
                                    />
                                </span>
                            </Tippy>
                        ) : (
                            <span
                                onClick={() => {
                                    if (!isRestoreDisabled) {
                                        onRestoreExtract(data);
                                    }
                                }}
                                style={{ cursor: isRestoreDisabled ? 'not-allowed' : 'pointer' }}
                            >
                                <img
                                    alt="Restore"
                                    src={isRestoreDisabled ? RestoreIconDisabled : RestoreIcon}
                                    style={{ marginRight: '10px', height: '21px', width: '21px' }}
                                />
                            </span>
                        )}

                        {/* Below will be implemented as part of BYZ-15015 */}

                        {/* Delete Action */}
                        {
                            isDisabled ? (
                                <Tippy
                                    content={
                                        !isUserMode()
                                            ? DELETED_ACTION_HOVER_MESSAGE
                                            : DAYS_PASSED_DELETE_ACTION_HOVER_MESSAGE
                                    }
                                >
                                    <span
                                        style={{ cursor: 'not-allowed' }}
                                    >
                                        <img
                                            alt="Delete"
                                            src={DeleteIconDisabled}
                                            style={{ height: '21px', width: '21px' }}
                                        />
                                    </span>
                                </Tippy>
                            ) : (
                                <span
                                    onClick={() => {
                                        permanentlyDeleteExtract(data?.id);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <img
                                        alt="Delete"
                                        src={DeleteIcon}
                                        style={{ height: '21px', width: '21px' }}
                                    />
                                </span>)
                        }
                    </div>
                );
            },
            disableHide: true,
        },
    ]);

    return (
        <>
            <ByzzerMask show={loading} loading={loading}>
                Loading your deleted extracts...
            </ByzzerMask>
            <div className={`${baseClass}__byzzer-table-container`}>
                <div className={`${baseClass}__byzzer-table-wrapper`}>
                    <ByzzerTable
                        ref={gridRef}
                        rowData={deletedExtracts}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        rowSelection='multiple'
                        suppressRowClickSelection={true}
                        tableArea={'dodDeletedTable'}
                        enableColumnHideShow={true}
                        enableSaveLayout={true}
                    />
                </div>
            </div>
        </>
    );
};
export default DodDeletedTable;
